import React, { useState, useEffect, useRef } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import SaveIcon from '@mui/icons-material/Save';
import SyncIcon from '@mui/icons-material/Sync';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import CodeIcon from '@mui/icons-material/Code';
import DifferenceIcon from '@mui/icons-material/Difference';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import IconButton from '@mui/material/IconButton';
import Editor, { DiffEditor } from '@monaco-editor/react';
import config from '../config';
import { useRules } from '../context/RuleContext';
import { parseXmlToRuleSet } from '../utils/xmlParser';
import { validateRuleSet } from '../utils/validator';
import { toast } from 'react-toastify';
import { XMLParser } from 'fast-xml-parser';

// Collapsible XML Tree Node
function XmlTreeNode({ name, value, depth = 0 }) {
  const [expanded, setExpanded] = useState(depth < 2);

  const isLeaf = typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean' || value === null || value === undefined;
  const isArray = Array.isArray(value);
  const isObject = value && typeof value === 'object' && !isArray;

  if (isLeaf) {
    return (
      <Box sx={{ pl: depth * 2, py: 0.2, fontFamily: 'monospace', fontSize: 13, display: 'flex', alignItems: 'center' }}>
        <Box sx={{ width: 20 }} />
        <span style={{ color: '#569cd6' }}>&lt;{name}&gt;</span>
        <span style={{ color: '#ce9178', fontWeight: 500, mx: 2 }}>{String(value ?? '')}</span>
        <span style={{ color: '#569cd6' }}>&lt;/{name}&gt;</span>
      </Box>
    );
  }

  if (isObject && Object.keys(value).every(k => k.startsWith('@_'))) {
    return (
      <Box sx={{ pl: depth * 2, py: 0.2, fontFamily: 'monospace', fontSize: 13 }}>
        <Box sx={{ width: 20, display: 'inline-block' }} />
        <span style={{ color: '#569cd6' }}>&lt;{name}</span>
        {Object.entries(value).map(([k, v]) => (
          <span key={k} style={{ color: '#9cdcfe' }}> {k.replace('@_', '')}=<span style={{ color: '#ce9178' }}>"{v}"</span></span>
        ))}
        <span style={{ color: '#569cd6' }}>/&gt;</span>
      </Box>
    );
  }

  const children = isArray ? value : (isObject ? Object.entries(value).filter(([k]) => !k.startsWith('@_')) : []);
  const nodeAttrs = isObject ? Object.entries(value).filter(([k]) => k.startsWith('@_')) : [];
  const attrStr = nodeAttrs.map(([k, v]) => `${k.replace('@_', '')}="${v}"`).join(' ');
  const childCount = isArray ? value.length : children.length;

  return (
    <Box>
      <Box
        sx={{ pl: depth * 2, py: 0.2, fontFamily: 'monospace', fontSize: 13, display: 'flex', alignItems: 'center', cursor: 'pointer', '&:hover': { bgcolor: 'rgba(255,255,255,0.03)' } }}
        onClick={() => setExpanded(!expanded)}
      >
        <IconButton size="small" sx={{ p: 0, width: 20, color: '#888' }}>
          {expanded ? <ExpandMoreIcon sx={{ fontSize: 14 }} /> : <ChevronRightIcon sx={{ fontSize: 14 }} />}
        </IconButton>
        <span style={{ color: '#569cd6' }}>&lt;{name}</span>
        {attrStr && <span style={{ color: '#9cdcfe' }}> {attrStr}</span>}
        <span style={{ color: '#569cd6' }}>&gt;</span>
        {!expanded && <span style={{ color: '#666', marginLeft: 4, fontSize: 11 }}>({childCount})</span>}
      </Box>
      {expanded && (
        <>
          {isArray
            ? value.map((item, idx) => <XmlTreeNode key={idx} name={`${name}`} value={item} depth={depth + 1} />)
            : children.map(([key, val]) => {
                if (Array.isArray(val)) return val.map((item, idx) => <XmlTreeNode key={`${key}-${idx}`} name={key} value={item} depth={depth + 1} />);
                return <XmlTreeNode key={key} name={key} value={val} depth={depth + 1} />;
              })
          }
          <Box sx={{ pl: depth * 2, py: 0.2, fontFamily: 'monospace', fontSize: 13 }}>
            <Box sx={{ width: 20, display: 'inline-block' }} />
            <span style={{ color: '#569cd6' }}>&lt;/{name}&gt;</span>
          </Box>
        </>
      )}
    </Box>
  );
}

function formatXml(xml) {
  let formatted = '';
  let indent = '';
  xml.replace(/>\s*</g, '>\n<').split('\n').forEach(line => {
    if (line.match(/^<\/\w/)) indent = indent.substring(4);
    formatted += indent + line.trim() + '\n';
    if (line.match(/^<\w[^>]*[^/]>/) && !line.match(/^<\?/) && !line.match(/<\/\w/)) indent += '    ';
  });
  return formatted.trim();
}

export default function RawEditor({ user }) {
  const { exportToXml, loadFromXml, fetchFromDb, loaded } = useRules();
  const [content, setContent] = useState('');
  const [originalContent, setOriginalContent] = useState('');
  const [errors, setErrors] = useState([]);
  const [saving, setSaving] = useState(false);
  const [dbInfo, setDbInfo] = useState(null);
  const [viewMode, setViewMode] = useState(0);
  const [parsedTree, setParsedTree] = useState(null);
  const [showDiffDialog, setShowDiffDialog] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const editorRef = useRef(null);

  // Mirror the shared rule state (used by AI Assistant and other pages) whenever it
  // changes, as long as the user has no unsaved edits in the editor
  const dirtyRef = useRef(false);
  dirtyRef.current = content !== originalContent;
  useEffect(() => {
    if (!loaded || dirtyRef.current) return;
    const xml = exportToXml();
    setContent(xml);
    setOriginalContent(xml);
  }, [exportToXml, loaded]);

  useEffect(() => {
    fetch(`${config.API_BASE}/rulefile`).then(r => r.json()).then(data => { if (data.success) setDbInfo(data); }).catch(() => {});
  }, []);

  useEffect(() => {
    if (viewMode === 1 && content) {
      try {
        const parser = new XMLParser({ ignoreAttributes: false, attributeNamePrefix: '@_', allowBooleanAttributes: true, isArray: (name) => ['LocationGroup', 'CarrierGroup', 'FeeRule', 'Location', 'Carrier'].includes(name) });
        setParsedTree(parser.parse(content));
      } catch { setParsedTree(null); }
    }
  }, [viewMode, content]);

  const handleEditorMount = (editor) => { editorRef.current = editor; };

  const handleLoadFromDb = async () => {
    try {
      const res = await fetch(`${config.API_BASE}/rulefile`);
      const data = await res.json();
      if (data.success) { setContent(data.content); setOriginalContent(data.content); toast.success('Loaded from DB'); }
      else { toast.error(data.message || 'No file in DB'); }
    } catch { toast.error('Backend not available'); }
  };

  const handleFormat = () => {
    try { setContent(formatXml(content)); toast.success('Formatted'); } catch { toast.error('Format failed'); }
  };

  const handleValidate = () => {
    setErrors([]);
    try {
      const parsed = parseXmlToRuleSet(content);
      const errs = validateRuleSet(parsed);
      if (errs.length > 0) { setErrors(errs); toast.error(`${errs.length} errors`); }
      else { toast.success(`Valid! ${parsed.feeRules?.length || 0} rules`); }
    } catch (err) { setErrors([`Parse Error: ${err.message}`]); }
  };

  const handleApply = () => {
    setErrors([]);
    try { const result = loadFromXml(content); if (!result.success) setErrors(result.errors || []); }
    catch (err) { setErrors([err.message]); }
  };

  const handleShowDiff = () => { setShowDiffDialog(true); };

  const handleSaveToDb = async () => {
    if (user?.role !== 'ADMIN') { toast.error('Only ADMIN can save'); return; }
    try {
      const parsed = parseXmlToRuleSet(content);
      const errs = validateRuleSet(parsed);
      if (errs.length > 0) { setErrors(errs); toast.error('Fix errors first'); return; }
    } catch (err) { setErrors([err.message]); return; }

    // Show diff before saving
    setShowDiffDialog(true);
  };

  const confirmSave = async () => {
    setShowDiffDialog(false);
    setSaving(true);
    try {
      const res = await fetch(`${config.API_BASE}/rulefile`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content, username: user?.username, role: user?.role }),
      });
      const data = await res.json();
      if (data.success) {
        toast.success(`Saved (v${data.version})`);
        setOriginalContent(content);
        setDbInfo((prev) => ({ ...prev, version: data.version }));
        // Sync the shared rule state so AI Assistant and other pages see the saved file
        await fetchFromDb();
      }
      else { toast.error(data.message); }
    } catch { toast.error('Backend error'); }
    finally { setSaving(false); }
  };

  const hasChanges = content !== originalContent;

  return (
    <Box sx={{
      display: 'flex', flexDirection: 'column',
      height: fullscreen ? '100vh' : 'calc(100vh - 130px)',
      ...(fullscreen && {
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1300, bgcolor: '#fff', p: 2,
      }),
    }}>
      {/* Toolbar */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>Raw Editor</Typography>
          {hasChanges && <Chip label="Unsaved" size="small" color="warning" />}
          {dbInfo && <Chip label={`DB v${dbInfo.version}`} size="small" variant="outlined" />}
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Button size="small" variant="outlined" startIcon={<SyncIcon />} onClick={handleLoadFromDb}>Load DB</Button>
          <Button size="small" variant="outlined" startIcon={<FormatAlignLeftIcon />} onClick={handleFormat}>Format</Button>
          <Button size="small" variant="outlined" onClick={handleValidate}>Validate</Button>
          {hasChanges && <Button size="small" variant="outlined" startIcon={<DifferenceIcon />} onClick={handleShowDiff}>Diff</Button>}
          <Button size="small" variant="contained" color="warning" onClick={handleApply}>Apply</Button>
          {user?.role === 'ADMIN' && (
            <Button size="small" variant="contained" color="success"
              startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
              onClick={handleSaveToDb} disabled={saving || !hasChanges}>Save DB</Button>
          )}
          <IconButton onClick={() => setFullscreen(!fullscreen)} color="primary" title={fullscreen ? 'Exit Fullscreen' : 'Fullscreen'}>
            {fullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
          </IconButton>
        </Box>
      </Box>

      {/* Hint */}
      <Alert severity="info" sx={{ mb: 1, py: 0 }} icon={false}>
        <Typography variant="caption">
          <strong>Ctrl+F</strong> Find | <strong>Ctrl+H</strong> Find & Replace | <strong>Ctrl+Z</strong> Undo | Full VS Code editing experience
        </Typography>
      </Alert>

      {errors.length > 0 && (
        <Alert severity="error" sx={{ mb: 1, py: 0.5 }}>
          <Typography variant="caption">{errors.length} error(s): {errors[0]}{errors.length > 1 ? ` (+${errors.length - 1} more)` : ''}</Typography>
        </Alert>
      )}

      {/* View Mode Tabs */}
      <Tabs value={viewMode} onChange={(e, v) => setViewMode(v)} sx={{ minHeight: 32, mb: 0.5 }}>
        <Tab icon={<CodeIcon sx={{ fontSize: 16 }} />} label="Editor" sx={{ minHeight: 32, py: 0, fontSize: 12 }} />
        <Tab icon={<AccountTreeIcon sx={{ fontSize: 16 }} />} label="Tree" sx={{ minHeight: 32, py: 0, fontSize: 12 }} />
      </Tabs>

      {/* Editor */}
      {viewMode === 0 && (
        <Box sx={{ flex: 1, border: '1px solid #333', borderRadius: 1, overflow: 'hidden' }}>
          <Editor
            height="100%"
            defaultLanguage="xml"
            value={content}
            onChange={(val) => setContent(val || '')}
            onMount={handleEditorMount}
            theme="vs-dark"
            options={{
              minimap: { enabled: true },
              fontSize: 13,
              wordWrap: 'on',
              lineNumbers: 'on',
              folding: true,
              foldingStrategy: 'indentation',
              automaticLayout: true,
              scrollBeyondLastLine: false,
              renderWhitespace: 'selection',
              tabSize: 4,
            }}
          />
        </Box>
      )}

      {/* Tree View */}
      {viewMode === 1 && (
        <Box sx={{ flex: 1, bgcolor: '#1e1e1e', color: '#d4d4d4', borderRadius: 1, overflow: 'auto', p: 2 }}>
          {parsedTree ? (
            Object.entries(parsedTree).map(([key, value]) => <XmlTreeNode key={key} name={key} value={value} depth={0} />)
          ) : (
            <Typography color="error">Cannot parse XML. Fix errors in editor first.</Typography>
          )}
        </Box>
      )}

      {/* Diff Dialog */}
      <Dialog open={showDiffDialog} onClose={() => setShowDiffDialog(false)} maxWidth="xl" fullWidth>
        <DialogTitle>Changes Review</DialogTitle>
        <DialogContent sx={{ p: 0, height: '70vh' }}>
          <DiffEditor
            height="100%"
            language="xml"
            original={originalContent}
            modified={content}
            theme="vs-dark"
            options={{ readOnly: true, renderSideBySide: true, fontSize: 13 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDiffDialog(false)}>Close</Button>
          {user?.role === 'ADMIN' && (
            <Button variant="contained" color="success" onClick={confirmSave}>Confirm & Save to DB</Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
